import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Button, Table, Select } from 'antd';
import ApexCharts from 'apexcharts';
import { analysisService } from '../../../../api/services/analysisService';
import PrimaryButton from '../../../../components/buttons/PrimaryButton';
import PrimarySquareBtn from '../../../../components/buttons/PrimarySquareBtn';
import { FormNumber, getRules } from '../../../../components/form/FormBasic';
import { SimpahProfitUnitType } from '../../../../enums/SimpahProfitUnitType';
import { referenceDataSyncService } from '../../../../services/referenceDataSyncService';
import dayjs from 'dayjs';
import ChartComponent, { CHART_TYPES } from '../../../../components/chartComponent/ChartComponent';
import { Trash, InfoCircle } from 'react-bootstrap-icons';
import { useNotificationContext } from '../../../../contexts/NotificationContext';
import { downloadCSV } from '../../../../helpers/utilityHelper';
import { useIsOnlineContext } from '../../../../contexts/IsOnlineContext';

const INITIAL_PRESENTATION_STATE = {
  presentation: undefined,
  productionCost: '',
  otherCosts: '',
  stage1: '',
  stage2: '',
  stage3: '',
};

const ProfitMarginAnalyticsTab = () => {
  const { t } = useTranslation(['advancedAnalysis']);
  const { successNotification, errorNotification, warningNotification } = useNotificationContext();
  const { isOnline } = useIsOnlineContext();
  const chartId = 'simpahDistributionChainChart';
  // Advanced Analytics Utility Config states
  const [configForm] = Form.useForm();
  const [allPresentationsList, setAllPresentationsList] = useState<
    { label: string; value: string }[]
  >([]);
  const [loadingAllPresentations, setLoadingAllPresentations] = useState(false);
  const [localPresentations, setLocalPresentations] = useState<any[]>([]);
  const [savedTableData, setSavedTableData] = useState<any[]>([]);
  const [savedGraphData, setSavedGraphData] = useState<any>(null);
  const [showTable, setShowTable] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [submittingModel, setSubmittingModel] = useState(false);
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);
  const [isDownloadingPNG, setIsDownloadingPNG] = useState(false);

  useEffect(() => {
    const fetchAllPresentations = async () => {
      try {
        setLoadingAllPresentations(true);
        const allPresentationsData = await referenceDataSyncService.getPresentations();
        if (allPresentationsData && Array.isArray(allPresentationsData)) {
          setAllPresentationsList(
            allPresentationsData.map((presentation: any) => ({
              label: presentation.name,
              value: presentation.refId,
            }))
          );
        }
      } catch (error: any) {
        console.error(error);
        errorNotification(error?.message || t('errorFetchingPresentations'));
      } finally {
        setLoadingAllPresentations(false);
      }
    };
    fetchAllPresentations();
  }, []);

  useEffect(() => {
    configForm.setFieldsValue({
      presentations: [{ ...INITIAL_PRESENTATION_STATE }],
    });
  }, []);

  const handlePresentationRowChange = (val: string, index: number) => {
    const existing = localPresentations.find((p) => p.presentation === val);
    const presentations = configForm.getFieldValue('presentations') || [];
    const currentProdCost =
      presentations.find((p: any) => p && p.productionCost)?.productionCost || '';
    if (existing) {
      presentations[index] = {
        presentation: val,
        productionCost: existing.productionCost,
        otherCosts: existing.otherCosts || '',
        stage1: existing.stage1Price,
        stage2: existing.stage2Price,
        stage3: existing.stage3Price,
      };
    } else {
      presentations[index] = {
        ...INITIAL_PRESENTATION_STATE,
        presentation: val,
        productionCost: currentProdCost,
      };
    }
    configForm.setFieldsValue({ presentations });
  };

  const handleValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues.presentations) {
      const changedIndex = changedValues.presentations.findIndex(
        (p: any) => p && p.productionCost !== undefined
      );
      if (changedIndex > -1) {
        const newCost = changedValues.presentations[changedIndex].productionCost;
        const updatedPresentations = allValues.presentations.map((p: any, idx: number) => {
          if (idx !== changedIndex && p) {
            return { ...p, productionCost: newCost };
          }
          return p;
        });
        configForm.setFieldsValue({ presentations: updatedPresentations });
      }
    }
  };

  const handleSelectedPresentationChange = (_val: string) => {
    configForm.setFieldsValue({
      presentations: [{ ...INITIAL_PRESENTATION_STATE }],
    });
    setLocalPresentations([]);
    setShowTable(false);
    setShowChart(false);
  };

  const onEnterFinish = async (values: any) => {
    if (!values.presentations || values.presentations.length === 0) {
      warningNotification(t('enterAtLeastOneConfig'));
      return;
    }

    const presentationVal = configForm.getFieldValue('presentation');
    if (!presentationVal) {
      warningNotification(t('selectPresentationAlert'));
      return;
    }

    const updated = values.presentations.map((p: any) => ({
      presentation: p.presentation,
      productionCost: Number(p.productionCost),
      otherCosts:
        p.otherCosts !== undefined && p.otherCosts !== '' ? Number(p.otherCosts) : undefined,
      stage1Price: Number(p.stage1),
      stage2Price: Number(p.stage2),
      stage3Price: Number(p.stage3),
    }));

    setLocalPresentations(updated);

    try {
      const payload = {
        productRefId: presentationVal,
        presentations: updated.map((p: any) => {
          return {
            presentation: p.presentation,
            productionCost: p.productionCost,
            otherCosts: p.otherCosts,
            stage1Price: p.stage1Price,
            stage2Price: p.stage2Price,
            stage3Price: p.stage3Price,
          };
        }),
      };

      await analysisService.saveUtilityConfig(payload);
      successNotification(t('utilityConfigSuccess'));
    } catch (error: any) {
      console.error(error);
      errorNotification(error?.message || t('modelCreateFailed'));
    }
  };

  const handleSaveAndFetch = async () => {
    if (localPresentations.length === 0) {
      warningNotification(t('enterAtLeastOneConfig'));
      return;
    }

    const presentationVal = configForm.getFieldValue('presentation');
    if (!presentationVal) {
      warningNotification(t('selectPresentationAlert'));
      return;
    }

    try {
      setSubmittingModel(true);
      const selectedPresentations = localPresentations.map((p: any) => p.presentation);

      const res = await analysisService.getUtilityConfigWithProfits(
        presentationVal,
        selectedPresentations
      );
      setSavedTableData(res.tableData);
      setSavedGraphData(res.graphData);
      setShowTable(true);
      successNotification(t('modelCreatedSuccess'));
    } catch (error: any) {
      console.error(error);
      errorNotification(error?.message || t('modelCreateFailed'));
    } finally {
      setSubmittingModel(false);
    }
  };

  const getSelectedPresentationLabel = () =>
    allPresentationsList.find((p) => p.value === configForm.getFieldValue('presentation'))?.label ||
    'Producto';

  const getDistributionChainChartTitle = () => {
    const separator = t('monthSeparator');
    const formatString = separator ? `MMMM [${separator}] YYYY` : 'MMMM YYYY';
    const monthNameSpan = dayjs().format(formatString);
    const formattedMonthSpan = monthNameSpan.charAt(0).toUpperCase() + monthNameSpan.slice(1);

    return {
      title: t('chartTitlePrefix'),
      subtitle: `${getSelectedPresentationLabel()} - ${formattedMonthSpan}`,
    };
  };

  const buildFilterOptionsForCSVExport = () => [
    {
      key: 'presentation',
      label: t('presentations'),
      value: getSelectedPresentationLabel(),
    },
    {
      key: 'presentation',
      label: t('presentationLabel'),
      value: localPresentations.map((p: any) => t(p.presentation)),
    },
  ];

  const escapeCsvValue = (value: any) => {
    const escaped = String(value ?? '').replace(/"/g, '""');
    return `"${escaped}"`;
  };

  const formatCurrency = (value: any) =>
    value !== null && value !== undefined ? `L${Number(value).toFixed(2)}` : '';

  const formatPercent = (value: any) =>
    value !== null && value !== undefined ? `${Number(value).toFixed(2)}%` : '';

  const getDistributionChainTableCSVContent = () => {
    const q1P = savedTableData[0]?.quintalPrice;
    const q2P = savedTableData[1]?.quintalPrice;
    const q3P = savedTableData[2]?.quintalPrice;
    const q1U = savedTableData[0]?.quintalProfit;
    const q2U = savedTableData[1]?.quintalProfit;
    const q3U = savedTableData[2]?.quintalProfit;

    const m1P = savedTableData[0]?.medidaPrice;
    const m2P = savedTableData[1]?.medidaPrice;
    const m3P = savedTableData[2]?.medidaPrice;
    const m1U = savedTableData[0]?.medidaProfit;
    const m2U = savedTableData[1]?.medidaProfit;
    const m3U = savedTableData[2]?.medidaProfit;

    const l1P = savedTableData[0]?.libraPrice;
    const l2P = savedTableData[1]?.libraPrice;
    const l3P = savedTableData[2]?.libraPrice;
    const l1U = savedTableData[0]?.libraProfit;
    const l2U = savedTableData[1]?.libraProfit;
    const l3U = savedTableData[2]?.libraProfit;

    const prodCost = localPresentations[0]?.productionCost?.toFixed(2) || '0.00';
    const otherCosts =
      localPresentations
        .find((p: any) => p.presentation === SimpahProfitUnitType.QUINTAL)
        ?.otherCosts?.toFixed(2) || '0.00';

    const rows = [
      [
        `${t('productionCostLabel')} = L${prodCost}; ${t('otherCostsLabel')} = L${otherCosts}`,
        t('tableHeaderProducerToMarkets'),
        t('tableHeaderMarketsToDistributors'),
        t('tableHeaderDistributorsToConsumers'),
      ],
    ];

    if (q1U !== undefined) {
      rows.push([
        t('tableHeaderUtility'),
        formatPercent(q1U),
        formatPercent(q2U),
        formatPercent(q3U),
      ]);
      rows.push([
        t(SimpahProfitUnitType.QUINTAL),
        formatCurrency(q1P),
        formatCurrency(q2P),
        formatCurrency(q3P),
      ]);
    }
    if (m1U !== undefined) {
      rows.push([
        t('tableHeaderUtility'),
        formatPercent(m1U),
        formatPercent(m2U),
        formatPercent(m3U),
      ]);
      rows.push([
        t(SimpahProfitUnitType.MEDIDA),
        formatCurrency(m1P),
        formatCurrency(m2P),
        formatCurrency(m3P),
      ]);
    }
    if (l1U !== undefined) {
      rows.push([
        t('tableHeaderUtility'),
        formatPercent(l1U),
        formatPercent(l2U),
        formatPercent(l3U),
      ]);
      rows.push([
        t(SimpahProfitUnitType.POUND),
        formatCurrency(l1P),
        formatCurrency(l2P),
        formatCurrency(l3P),
      ]);
    }

    let csvContent = `${t('filtersUsed')}\n`;
    buildFilterOptionsForCSVExport().forEach((filter) => {
      csvContent += `${escapeCsvValue(filter.label)},${escapeCsvValue(filter.value)}\n`;
    });

    csvContent += `\n${t('chartData')}\n`;
    csvContent += rows.map((row) => row.map(escapeCsvValue).join(',')).join('\n');

    return csvContent;
  };

  const downloadPNG = async () => {
    const dataURI = await ApexCharts.exec(chartId, 'dataURI');

    const link = document.createElement('a');
    link.href = dataURI.imgURI;
    link.download = `${t('distributionChainModel')}_${Date.now()}.png`;
    link.click();
  };

  const handleDownloadPNG = async () => {
    if (isDownloadingPNG) return;

    setIsDownloadingPNG(true);
    try {
      await downloadPNG();
    } catch (error) {
      console.error('Error downloading PNG:', error);
    } finally {
      setTimeout(() => setIsDownloadingPNG(false), 500);
    }
  };

  const handleDownloadCSV = () => {
    if (isDownloadingCSV) return;

    setIsDownloadingCSV(true);
    try {
      downloadCSV(getDistributionChainTableCSVContent(), t('distributionChainModel'));
    } finally {
      setTimeout(() => setIsDownloadingCSV(false), 500);
    }
  };

  const renderResultsTable = () => {
    if (!savedTableData || savedTableData.length < 3) return null;

    const q1P = savedTableData[0]?.quintalPrice;
    const q2P = savedTableData[1]?.quintalPrice;
    const q3P = savedTableData[2]?.quintalPrice;
    const q1U = savedTableData[0]?.quintalProfit;
    const q2U = savedTableData[1]?.quintalProfit;
    const q3U = savedTableData[2]?.quintalProfit;

    const m1P = savedTableData[0]?.medidaPrice;
    const m2P = savedTableData[1]?.medidaPrice;
    const m3P = savedTableData[2]?.medidaPrice;
    const m1U = savedTableData[0]?.medidaProfit;
    const m2U = savedTableData[1]?.medidaProfit;
    const m3U = savedTableData[2]?.medidaProfit;

    const l1P = savedTableData[0]?.libraPrice;
    const l2P = savedTableData[1]?.libraPrice;
    const l3P = savedTableData[2]?.libraPrice;
    const l1U = savedTableData[0]?.libraProfit;
    const l2U = savedTableData[1]?.libraProfit;
    const l3U = savedTableData[2]?.libraProfit;

    const prodCost = localPresentations[0]?.productionCost?.toFixed(2) || '0.00';
    const otherCosts =
      localPresentations
        .find((p: any) => p.presentation === SimpahProfitUnitType.QUINTAL)
        ?.otherCosts?.toFixed(2) || '0.00';

    const columns = [
      {
        title: (
          <div className="flex flex-col whitespace-nowrap">
            <span>
              {t('productionCostLabel')} = L{prodCost}
            </span>
            <span>
              {t('otherCostsLabel')} = L{otherCosts}
            </span>
          </div>
        ),
        dataIndex: 'col1',
        key: 'col1',
        align: 'left' as const,
      },
      {
        title: t('tableHeaderProducerToMarkets'),
        dataIndex: 'col2',
        key: 'col2',
        align: 'center' as const,
      },
      {
        title: t('tableHeaderMarketsToDistributors'),
        dataIndex: 'col3',
        key: 'col3',
        align: 'center' as const,
      },
      {
        title: t('tableHeaderDistributorsToConsumers'),
        dataIndex: 'col4',
        key: 'col4',
        align: 'center' as const,
      },
    ];

    const dataSource: any[] = [];
    if (q1U !== undefined) {
      dataSource.push({
        key: 'q-utility',
        isUtility: true,
        col1: <span className="font-bold">{t('tableHeaderUtility')}</span>,
        col2: <span className="font-bold">{formatPercent(q1U)}</span>,
        col3: <span className="font-bold">{formatPercent(q2U)}</span>,
        col4: <span className="font-bold">{formatPercent(q3U)}</span>,
      });
      dataSource.push({
        key: 'q-price',
        col1: <span className="font-bold">{t(SimpahProfitUnitType.QUINTAL)}</span>,
        col2: <span className="font-semibold">{formatCurrency(q1P)}</span>,
        col3: <span className="font-semibold">{formatCurrency(q2P)}</span>,
        col4: <span className="font-semibold">{formatCurrency(q3P)}</span>,
      });
    }
    if (m1U !== undefined) {
      dataSource.push({
        key: 'm-utility',
        isUtility: true,
        col1: <span className="font-bold">{t('tableHeaderUtility')}</span>,
        col2: <span className="font-bold">{formatPercent(m1U)}</span>,
        col3: <span className="font-bold">{formatPercent(m2U)}</span>,
        col4: <span className="font-bold">{formatPercent(m3U)}</span>,
      });
      dataSource.push({
        key: 'm-price',
        col1: <span className="font-bold">{t(SimpahProfitUnitType.MEDIDA)}</span>,
        col2: <span className="font-semibold">{formatCurrency(m1P)}</span>,
        col3: <span className="font-semibold">{formatCurrency(m2P)}</span>,
        col4: <span className="font-semibold">{formatCurrency(m3P)}</span>,
      });
    }
    if (l1U !== undefined) {
      dataSource.push({
        key: 'l-utility',
        isUtility: true,
        col1: <span className="font-bold">{t('tableHeaderUtility')}</span>,
        col2: <span className="font-bold">{formatPercent(l1U)}</span>,
        col3: <span className="font-bold">{formatPercent(l2U)}</span>,
        col4: <span className="font-bold">{formatPercent(l3U)}</span>,
      });
      dataSource.push({
        key: 'l-price',
        col1: <span className="font-bold">{t(SimpahProfitUnitType.POUND)}</span>,
        col2: <span className="font-semibold">{formatCurrency(l1P)}</span>,
        col3: <span className="font-semibold">{formatCurrency(l2P)}</span>,
        col4: <span className="font-semibold">{formatCurrency(l3P)}</span>,
      });
    }

    return (
      <div className="overflow-x-auto">
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          bordered
          rowClassName={(record) => (record.isUtility ? 'bg-gray-50' : '')}
          className="min-w-full font-sans text-xs"
        />
      </div>
    );
  };

  return (
    <div className="mb-20 mt-8">
      {/* Create Distribution Chain Model Form Section */}
      <section className="mt-20 rounded-lg border border-border-color bg-white p-6 shadow-sm">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800">
            {t('createDistributionChainModel')}
          </h3>
        </div>

        <Form
          form={configForm}
          layout="vertical"
          onFinish={onEnterFinish}
          onValuesChange={handleValuesChange}
          className="space-y-4"
        >
          {/* Presentation Dropdown */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <Form.Item
              label={t('presentations')}
              name="presentation"
              rules={getRules(t, false, [{ required: true, message: t('presentationRequired') }])}
            >
              <Select
                showSearch
                loading={loadingAllPresentations}
                placeholder={t('presentationsPlaceholder')}
                options={allPresentationsList}
                onChange={handleSelectedPresentationChange}
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                className="w-full [&>.ant-select-selector]:!rounded-lg"
              />
            </Form.Item>
          </div>

          {/* Form List of Presentations */}
          <Form.List name="presentations">
            {(fields, { add, remove }) => {
              const selectedPresentations =
                configForm.getFieldValue('presentations')?.map((p: any) => p?.presentation) || [];

              return (
                <div className="space-y-6">
                  {fields.map(({ key, name, ...restField }, idx) => {
                    const currentPresentation = configForm.getFieldValue([
                      'presentations',
                      name,
                      'presentation',
                    ]);
                    const isQuintal = currentPresentation === SimpahProfitUnitType.QUINTAL;

                    return (
                      <div
                        key={key}
                        className="relative space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-6"
                      >
                        {fields.length > 1 && (
                          <Button
                            type="text"
                            danger
                            onClick={() => remove(name)}
                            className="absolute right-4 top-4 z-10 flex items-center gap-1 text-red-500 hover:text-red-700"
                          >
                            <Trash size={16} />
                          </Button>
                        )}

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <Form.Item
                            {...restField}
                            label={t('presentationLabel')}
                            name={[name, 'presentation']}
                            rules={getRules(t, false, [
                              { required: true, message: t('presentationRequired') },
                            ])}
                          >
                            <Select
                              placeholder={t('presentationPlaceholder')}
                              options={[
                                {
                                  label: t(SimpahProfitUnitType.QUINTAL),
                                  value: SimpahProfitUnitType.QUINTAL,
                                  disabled:
                                    selectedPresentations.includes(SimpahProfitUnitType.QUINTAL) &&
                                    currentPresentation !== SimpahProfitUnitType.QUINTAL,
                                },
                                {
                                  label: t(SimpahProfitUnitType.MEDIDA),
                                  value: SimpahProfitUnitType.MEDIDA,
                                  disabled:
                                    selectedPresentations.includes(SimpahProfitUnitType.MEDIDA) &&
                                    currentPresentation !== SimpahProfitUnitType.MEDIDA,
                                },
                                {
                                  label: t(SimpahProfitUnitType.POUND),
                                  value: SimpahProfitUnitType.POUND,
                                  disabled:
                                    selectedPresentations.includes(SimpahProfitUnitType.POUND) &&
                                    currentPresentation !== SimpahProfitUnitType.POUND,
                                },
                              ]}
                              onChange={(val) => handlePresentationRowChange(val, name)}
                              className="w-full [&>.ant-select-selector]:!rounded-lg"
                            />
                          </Form.Item>
                        </div>

                        {/* Production Cost and Other Costs */}
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <FormNumber
                            label={t('productionCostLabel')}
                            name={[name, 'productionCost']}
                            type="float"
                            min="0"
                            rules={[{ required: true, message: t('productionCostRequired') }]}
                            placeholder={t('enterCostPlaceholder')}
                          />

                          <FormNumber
                            label={t('otherCostsLabel')}
                            name={[name, 'otherCosts']}
                            type="float"
                            min="0"
                            disabled={!isQuintal}
                            placeholder={t('enterCostPlaceholder')}
                          />
                        </div>

                        {/* Stage 1, Stage 2, Stage 3 */}
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                          <FormNumber
                            label={t('stage1Label')}
                            name={[name, 'stage1']}
                            type="float"
                            min="0"
                            rules={[{ required: true, message: t('stage1Required') }]}
                            placeholder={t('enterValuePlaceholder')}
                          />

                          <FormNumber
                            label={
                              <span className="flex items-center gap-1.5">
                                {t('stage2Label')}
                                <InfoCircle
                                  size={13}
                                  className="cursor-help text-blue-500"
                                  title={t('stage2Description')}
                                />
                              </span>
                            }
                            name={[name, 'stage2']}
                            type="float"
                            min="0"
                            rules={[{ required: true, message: t('stage2Required') }]}
                            placeholder={t('enterValuePlaceholder')}
                          />

                          <FormNumber
                            label={
                              <span className="flex items-center gap-1.5">
                                {t('stage3Label')}
                                <InfoCircle
                                  size={13}
                                  className="cursor-help text-blue-500"
                                  title={t('stage3Description')}
                                />
                              </span>
                            }
                            name={[name, 'stage3']}
                            type="float"
                            min="0"
                            rules={[{ required: true, message: t('stage3Required') }]}
                            placeholder={t('enterValuePlaceholder')}
                          />
                        </div>
                      </div>
                    );
                  })}

                  {/* Add Presentation Button */}
                  {fields.length < 3 && (
                    <div className="flex justify-start">
                      <Button
                        type="dashed"
                        onClick={() => {
                          const currentProdCost =
                            configForm
                              .getFieldValue('presentations')
                              ?.find((p: any) => p && p.productionCost)?.productionCost || '';
                          add({
                            ...INITIAL_PRESENTATION_STATE,
                            productionCost: currentProdCost,
                          });
                        }}
                        className="flex items-center gap-2 rounded-lg text-sm"
                      >
                        {t('addPresentationLabel')}
                      </Button>
                    </div>
                  )}
                </div>
              );
            }}
          </Form.List>

          {/* Enter Button */}
          <div className="flex justify-end pt-2">
            <PrimarySquareBtn
              handleOnClick={(e) => {
                e?.preventDefault();
                configForm.submit();
              }}
            >
              {t('enterLabel')}
            </PrimarySquareBtn>
          </div>
        </Form>
      </section>

      {/* Distribution Chain Model Section */}
      <section className="mt-20 border-t border-gray-200 pt-8">
        <h4 className="mb-6 text-xl font-semibold text-gray-800">{t('distributionChainModel')}</h4>
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <PrimaryButton
              handleOnClick={handleSaveAndFetch}
              disabled={submittingModel}
              className="w-full"
            >
              {t('createDistributionModelBtn')}
            </PrimaryButton>
          </div>
          <div className="lg:col-span-3">
            {showTable ? (
              <div className="flex flex-col gap-8">
                <div data-html2canvas-ignore="true" className="flex justify-end gap-3">
                  <PrimaryButton
                    disabled={isDownloadingCSV || !isOnline}
                    className="px-3"
                    handleOnClick={handleDownloadCSV}
                  >
                    {t('downloadProfitMarginTable')}
                  </PrimaryButton>
                </div>
                {renderResultsTable()}
                {savedGraphData && (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <div data-html2canvas-ignore="true" className="mb-4 flex justify-end gap-3">
                      <PrimaryButton
                        disabled={isDownloadingPNG || !isOnline}
                        className="px-3"
                        handleOnClick={handleDownloadPNG}
                      >
                        {t('downloadProfitMarginChart')}
                      </PrimaryButton>
                    </div>
                    <ChartComponent
                      title=""
                      series={(savedGraphData?.series || []).map((s: any) => {
                        let name = s.name;
                        if (s.name.includes('Producer') || s.name.includes('Productor')) {
                          name = t('chartProducer');
                        } else if (s.name.includes('Market') || s.name.includes('mercado')) {
                          name = t('chartMarket');
                        } else if (
                          s.name.includes('Distributors') ||
                          s.name.includes('Distribuidor')
                        ) {
                          name = t('chartDistributor');
                        }
                        return {
                          ...s,
                          name,
                        };
                      })}
                      options={{
                        chart: {
                          id: chartId,
                          type: 'bar',
                          toolbar: {
                            show: false,
                          },
                        },
                        title: {
                          text: getDistributionChainChartTitle().title,
                          align: 'center',
                          style: {
                            fontSize: '20px',
                            fontWeight: 600,
                          },
                        },
                        subtitle: {
                          text: getDistributionChainChartTitle().subtitle,
                          align: 'center',
                          style: {
                            fontSize: '16px',
                            fontWeight: 600,
                          },
                        },
                        plotOptions: {
                          bar: {
                            horizontal: false,
                            columnWidth: '55%',
                            borderRadius: 0,
                          },
                        },
                        colors: ['#8DC63F', '#00B0F0', '#FFC000'],
                        xaxis: {
                          categories: (savedGraphData?.categories || []).map((c: string) => {
                            const lowerC = c.toLowerCase();
                            if (lowerC === 'quintal') return t(SimpahProfitUnitType.QUINTAL);
                            if (lowerC === 'medida') return t(SimpahProfitUnitType.MEDIDA);
                            if (lowerC === 'libra' || lowerC === 'pound')
                              return t(SimpahProfitUnitType.POUND);
                            return c;
                          }),
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: (val: any) => `${val}`,
                          style: {
                            colors: ['#000000'],
                          },
                        },
                        legend: {
                          position: 'bottom',
                          horizontalAlign: 'center',
                        },
                        yaxis: {
                          title: {
                            text: t('chartYAxisLabel'),
                          },
                          labels: {
                            formatter: (val: any) => `${val}%`,
                          },
                        },
                      }}
                      type={CHART_TYPES.BAR}
                      height={400}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-400">
                {t('clickCreateDistributionModel')}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProfitMarginAnalyticsTab;
